import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material';

import DataUsageIcon from '@mui/icons-material/DataUsage';
import InsightsIcon from '@mui/icons-material/Insights';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import TuneIcon from '@mui/icons-material/Tune';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const BRAND = {
  blue: '#1e4b81',
  orange: '#d37d36',
  dark: '#111827',
  text: '#1F2937',
  muted: '#667085',
  softMuted: '#98A2B3',
  border: 'rgba(16, 24, 40, 0.12)',
  borderSoft: 'rgba(16, 24, 40, 0.07)',
  bg: '#F6F8FB',
  panel: '#FFFFFF',
};

const brandConfig = {
  Gobierno: {
    label: 'Gobierno del Dato',
    shortLabel: 'Gobierno',
    icon: <DataUsageIcon />,
  },
  Gestion: {
    label: 'Gestión del Dato',
    shortLabel: 'Gestión',
    icon: <InsightsIcon />,
  },
  Calidad: {
    label: 'Calidad del Dato',
    shortLabel: 'Calidad',
    icon: <WorkspacePremiumIcon />,
  },
  Generales: {
    label: 'Preguntas Generales',
    shortLabel: 'Generales',
    icon: <TuneIcon />,
  },
};

const getProgressColor = (score = 0) => {
  const value = Number(score || 0);

  if (value >= 80) return '#15803D';
  if (value >= 50) return BRAND.orange;
  return BRAND.blue;
};

const ScoreBar = ({ value, width = 120, showValue = true }) => {
  const score = Number(value || 0);
  const color = getProgressColor(score);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {showValue && (
        <Typography
          sx={{
            minWidth: 42,
            textAlign: 'right',
            color: BRAND.text,
            fontWeight: 750,
            fontSize: 12.5,
          }}
        >
          {score.toFixed(0)}%
        </Typography>
      )}

      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, score))}
        sx={{
          width,
          height: 6,
          borderRadius: 99,
          bgcolor: 'rgba(30, 75, 129, 0.10)',
          '& .MuiLinearProgress-bar': {
            bgcolor: color,
            borderRadius: 99,
          },
        }}
      />
    </Stack>
  );
};

const EmptyState = ({ title = 'Sin datos', text }) => (
  <Box
    sx={{
      py: 7,
      px: 3,
      textAlign: 'center',
      color: BRAND.muted,
      bgcolor: '#fff',
    }}
  >
    <InfoOutlinedIcon sx={{ mb: 1, color: BRAND.softMuted }} />
    <Typography sx={{ color: BRAND.dark, fontWeight: 800, mb: 0.5 }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: 14 }}>{text}</Typography>
  </Box>
);

const SummaryCard = ({ label, value, helper, children }) => (
  <Box
    sx={{
      p: 1.6,
      borderRadius: 2,
      bgcolor: '#FFFFFF',
      border: `1px solid ${BRAND.borderSoft}`,
    }}
  >
    <Typography
      sx={{
        color: BRAND.muted,
        fontSize: 11,
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        mb: 0.45,
      }}
    >
      {label}
    </Typography>

    {children || (
      <>
        <Typography
          sx={{
            color: BRAND.dark,
            fontSize: 20,
            fontWeight: 850,
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
          }}
        >
          {value}
        </Typography>

        {helper && (
          <Typography sx={{ color: BRAND.muted, fontSize: 12, mt: 0.35 }}>
            {helper}
          </Typography>
        )}
      </>
    )}
  </Box>
);

const ProcessListItem = ({ proceso, selected, onClick }) => {
  const score = Number(proceso.ponderacionMedia || 0);
  const questionCount = Array.isArray(proceso.preguntas) ? proceso.preguntas.length : 0;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          onClick();
        }
      }}
      sx={{
        cursor: 'pointer',
        px: 1.6,
        py: 1.35,
        borderRadius: 1.8,
        bgcolor: selected ? '#FFFFFF' : 'transparent',
        border: selected
          ? `1px solid ${alpha(BRAND.blue, 0.24)}`
          : '1px solid transparent',
        boxShadow: selected ? '0 6px 14px rgba(16, 24, 40, 0.06)' : 'none',
        transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          bgcolor: '#FFFFFF',
          borderColor: selected ? alpha(BRAND.blue, 0.24) : BRAND.borderSoft,
        },
      }}
    >
      <Stack direction="row" spacing={1.3} alignItems="flex-start">
        <Box
          sx={{
            width: 3,
            height: 42,
            borderRadius: 99,
            bgcolor: selected ? BRAND.blue : '#D0D5DD',
            mt: 0.15,
            flexShrink: 0,
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: BRAND.dark,
              fontWeight: selected ? 850 : 750,
              fontSize: 13.8,
              lineHeight: 1.35,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {proceso.nombre}
          </Typography>

          <Stack
            direction="row"
            spacing={0.8}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 0.75 }}
          >
            <Chip
              label={proceso.abreviatura}
              size="small"
              sx={{
                height: 21,
                fontSize: 11,
                fontWeight: 800,
                color: BRAND.blue,
                bgcolor: alpha(BRAND.blue, 0.07),
                border: `1px solid ${alpha(BRAND.blue, 0.12)}`,
              }}
            />

            <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>
              {questionCount} preguntas
            </Typography>
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
            sx={{ mt: 1.05 }}
          >
            <Chip
              label={`Nivel ${proceso.nivelCapacidad}`}
              size="small"
              sx={{
                height: 23,
                minWidth: 66,
                bgcolor: '#fff',
                color: BRAND.text,
                border: `1px solid ${BRAND.border}`,
                fontWeight: 800,
                fontSize: 11,
              }}
            />

            <ScoreBar value={score} width={72} />
          </Stack>
        </Box>

        {selected && (
          <ArrowForwardIosIcon
            sx={{
              color: BRAND.blue,
              fontSize: 14,
              mt: 1.2,
              display: { xs: 'none', md: 'block' },
            }}
          />
        )}
      </Stack>
    </Box>
  );
};

const QuestionsDetail = ({ proceso }) => {
  if (!proceso) {
    return (
      <EmptyState
        title="Selecciona un proceso"
        text="Elige un proceso de la lista para consultar sus preguntas y resultados."
      />
    );
  }

  const score = Number(proceso.ponderacionMedia || 0);
  const questions = proceso.preguntas || [];
  const questionCount = questions.length;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          px: { xs: 2, md: 2.6 },
          py: 2.3,
          borderBottom: `1px solid ${BRAND.borderSoft}`,
          bgcolor: '#FFFFFF',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'flex-start' }}
          spacing={2}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography
                sx={{
                  color: BRAND.dark,
                  fontSize: { xs: 18, md: 21 },
                  fontWeight: 850,
                  letterSpacing: '-0.035em',
                  lineHeight: 1.2,
                }}
              >
                {proceso.nombre}
              </Typography>

              <Chip
                label={proceso.abreviatura}
                size="small"
                sx={{
                  height: 23,
                  fontSize: 11,
                  fontWeight: 800,
                  color: BRAND.blue,
                  bgcolor: alpha(BRAND.blue, 0.07),
                  border: `1px solid ${alpha(BRAND.blue, 0.12)}`,
                }}
              />
            </Stack>

            <Typography sx={{ color: BRAND.muted, fontSize: 13.5, mt: 0.8 }}>
              Preguntas evaluadas y puntuación obtenida en este proceso.
            </Typography>
          </Box>

          <Chip
            label={`Nivel ${proceso.nivelCapacidad}`}
            size="small"
            sx={{
              height: 27,
              bgcolor: '#fff',
              color: BRAND.text,
              border: `1px solid ${BRAND.border}`,
              fontWeight: 850,
            }}
          />
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 1.3,
            mt: 2.3,
          }}
        >
          <SummaryCard
            label="Preguntas"
            value={questionCount}
            helper="Evaluadas en el proceso"
          />

          <SummaryCard
            label="Promedio"
            value={`${score.toFixed(1)}/100`}
            helper="Resultado consolidado"
          />

          <SummaryCard label="Avance">
            <Box sx={{ mt: 1 }}>
              <ScoreBar value={score} width="100%" />
            </Box>
          </SummaryCard>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          bgcolor: '#FBFCFE',
          p: { xs: 1.4, md: 1.8 },
          overflow: 'auto',
        }}
      >
        {questions.length > 0 ? (
          <TableContainer
            sx={{
              bgcolor: '#fff',
              border: `1px solid ${BRAND.borderSoft}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      py: 1.25,
                      bgcolor: '#F8FAFC',
                      borderBottom: `1px solid ${BRAND.border}`,
                      color: BRAND.muted,
                      fontSize: 11,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    },
                  }}
                >
                  <TableCell sx={{ pl: { xs: 2, md: 2.5 } }}>
                    Pregunta de evaluación
                  </TableCell>

                  <TableCell align="right" sx={{ width: 190, pr: { xs: 2, md: 2.5 } }}>
                    Resultado
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {questions.map((pregunta, index) => {
                  const questionScore = Number(pregunta.porcentaje || 0);

                  return (
                    <TableRow
                      key={pregunta.id || index}
                      sx={{
                        '& td': {
                          borderBottom: `1px solid ${BRAND.borderSoft}`,
                        },
                        '&:last-child td': {
                          borderBottom: 0,
                        },
                        '&:hover': {
                          bgcolor: '#FCFCFD',
                        },
                      }}
                    >
                      <TableCell sx={{ pl: { xs: 2, md: 2.5 }, py: 1.65 }}>
                        <Typography
                          sx={{
                            color: BRAND.text,
                            fontSize: 13.7,
                            lineHeight: 1.55,
                          }}
                        >
                          {pregunta.texto}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" sx={{ pr: { xs: 2, md: 2.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <ScoreBar value={questionScore} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: `1px solid ${BRAND.borderSoft}`,
              overflow: 'hidden',
            }}
          >
            <EmptyState text="No hay detalle de preguntas disponible para este proceso." />
          </Paper>
        )}
      </Box>
    </Box>
  );
};

const GeneralQuestionsDetail = ({ questions = [], promedio = 0 }) => {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          px: { xs: 2, md: 2.6 },
          py: 2.3,
          borderBottom: `1px solid ${BRAND.borderSoft}`,
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography
          sx={{
            color: BRAND.dark,
            fontSize: { xs: 18, md: 21 },
            fontWeight: 850,
            letterSpacing: '-0.035em',
            lineHeight: 1.2,
          }}
        >
          Preguntas generales de madurez
        </Typography>

        <Typography sx={{ color: BRAND.muted, fontSize: 13.5, mt: 0.8 }}>
          Evaluación transversal no asociada a un único proceso operativo.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 1.3,
            mt: 2.3,
          }}
        >
          <SummaryCard
            label="Preguntas"
            value={questions.length}
            helper="Preguntas transversales"
          />

          <SummaryCard
            label="Promedio"
            value={`${promedio.toFixed(1)}/100`}
            helper="Resultado consolidado"
          />

          <SummaryCard label="Avance">
            <Box sx={{ mt: 1 }}>
              <ScoreBar value={promedio} width="100%" />
            </Box>
          </SummaryCard>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          bgcolor: '#FBFCFE',
          p: { xs: 1.4, md: 1.8 },
          overflow: 'auto',
        }}
      >
        <TableContainer
          sx={{
            bgcolor: '#fff',
            border: `1px solid ${BRAND.borderSoft}`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    py: 1.25,
                    bgcolor: '#F8FAFC',
                    borderBottom: `1px solid ${BRAND.border}`,
                    color: BRAND.muted,
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  },
                }}
              >
                <TableCell sx={{ pl: { xs: 2, md: 2.5 } }}>
                  Pregunta de evaluación
                </TableCell>

                <TableCell align="right" sx={{ width: 190, pr: { xs: 2, md: 2.5 } }}>
                  Resultado
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {questions.map((pregunta, index) => {
                const questionScore = Number(pregunta.porcentaje || 0);

                return (
                  <TableRow
                    key={pregunta.id || index}
                    sx={{
                      '& td': {
                        borderBottom: `1px solid ${BRAND.borderSoft}`,
                      },
                      '&:last-child td': {
                        borderBottom: 0,
                      },
                    }}
                  >
                    <TableCell sx={{ pl: { xs: 2, md: 2.5 }, py: 1.65 }}>
                      <Typography
                        sx={{
                          color: BRAND.text,
                          fontSize: 13.7,
                          lineHeight: 1.55,
                        }}
                      >
                        {pregunta.texto}
                      </Typography>
                    </TableCell>

                    <TableCell align="right" sx={{ pr: { xs: 2, md: 2.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <ScoreBar value={questionScore} />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

const DetallePorProceso = ({
  userRole,
  resultadosPorProceso = [],
  resultadosGenerales = [],
}) => {
  const [tipoSeleccionado, setTipoSeleccionado] = useState('Gobierno');
  const [selectedProcesoId, setSelectedProcesoId] = useState(null);
  const [search, setSearch] = useState('');

  const canShowGenerales = userRole !== 'ROLE_FREEMIUM' && resultadosGenerales.length > 0;

  const tiposDisponibles = useMemo(() => {
    const tipos = new Set(resultadosPorProceso.map((p) => p.tipo));

    if (canShowGenerales) {
      tipos.add('Generales');
    }

    return Array.from(tipos);
  }, [resultadosPorProceso, canShowGenerales]);

  useEffect(() => {
    if (tiposDisponibles.length > 0 && !tiposDisponibles.includes(tipoSeleccionado)) {
      setTipoSeleccionado(tiposDisponibles[0]);
    }
  }, [tiposDisponibles, tipoSeleccionado]);

  const procesosBase = useMemo(() => {
    return resultadosPorProceso.filter((p) => p.tipo === tipoSeleccionado);
  }, [resultadosPorProceso, tipoSeleccionado]);

  const procesosFiltrados = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return procesosBase.filter((p) => {
      if (!normalizedSearch) return true;

      return (
        p.nombre?.toLowerCase().includes(normalizedSearch) ||
        p.abreviatura?.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [procesosBase, search]);

  useEffect(() => {
    if (tipoSeleccionado === 'Generales') {
      setSelectedProcesoId(null);
      return;
    }

    if (!procesosFiltrados.length) {
      setSelectedProcesoId(null);
      return;
    }

    const selectedStillExists = procesosFiltrados.some(
      (p) => p.procesoId === selectedProcesoId
    );

    if (!selectedStillExists) {
      setSelectedProcesoId(procesosFiltrados[0].procesoId);
    }
  }, [tipoSeleccionado, procesosFiltrados, selectedProcesoId]);

  const selectedProceso = useMemo(() => {
    return procesosFiltrados.find((p) => p.procesoId === selectedProcesoId) || null;
  }, [procesosFiltrados, selectedProcesoId]);

  const promedioGenerales = useMemo(() => {
    if (!resultadosGenerales.length) return 0;

    return (
      resultadosGenerales.reduce((acc, curr) => acc + Number(curr.porcentaje || 0), 0) /
      resultadosGenerales.length
    );
  }, [resultadosGenerales]);

  const resumenSeleccionado = useMemo(() => {
    if (tipoSeleccionado === 'Generales') {
      return {
        procesos: 0,
        preguntas: resultadosGenerales.length,
        promedio: promedioGenerales,
      };
    }

    const preguntas = procesosBase.reduce(
      (acc, proceso) => acc + (Array.isArray(proceso.preguntas) ? proceso.preguntas.length : 0),
      0
    );

    const promedio =
      procesosBase.length > 0
        ? procesosBase.reduce((acc, proceso) => acc + Number(proceso.ponderacionMedia || 0), 0) /
          procesosBase.length
        : 0;

    return {
      procesos: procesosBase.length,
      preguntas,
      promedio,
    };
  }, [tipoSeleccionado, procesosBase, resultadosGenerales.length, promedioGenerales]);

  const handleTipoChange = (_, newTipo) => {
    if (newTipo !== null) {
      setTipoSeleccionado(newTipo);
      setSearch('');
      setSelectedProcesoId(null);
    }
  };

  const selectedConfig = brandConfig[tipoSeleccionado] || brandConfig.Gobierno;

  return (
    <Box
      sx={{
        mt: 4,
        px: { xs: 0, md: 0 },
        width: '100%',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          overflow: 'hidden',
          borderRadius: 2.5,
          bgcolor: '#FFFFFF',
          border: `1px solid ${BRAND.border}`,
          boxShadow: '0 8px 22px rgba(16, 24, 40, 0.04)',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 2.6 },
            py: { xs: 2.3, md: 2.6 },
            bgcolor: '#FFFFFF',
          }}
        >
          <Typography
            sx={{
              color: BRAND.blue,
              fontWeight: 850,
              letterSpacing: '0.08em',
              fontSize: 11,
              textTransform: 'uppercase',
              mb: 0.8,
            }}
          >
            Detalle de evaluación
          </Typography>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'flex-end' }}
            spacing={2}
          >
            <Box sx={{ maxWidth: 820 }}>
              <Typography
                sx={{
                  color: BRAND.dark,
                  fontWeight: 850,
                  fontSize: { xs: 21, md: 25 },
                  letterSpacing: '-0.035em',
                  lineHeight: 1.18,
                }}
              >
                Resultados por proceso y pregunta
              </Typography>

              <Typography
                sx={{
                  color: BRAND.muted,
                  mt: 0.9,
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
              >
                Selecciona un proceso para consultar sus preguntas y el resultado obtenido.
              </Typography>
            </Box>

            <Chip
              label={selectedConfig.label}
              size="small"
              sx={{
                height: 28,
                bgcolor: alpha(BRAND.blue, 0.06),
                color: BRAND.blue,
                border: `1px solid ${alpha(BRAND.blue, 0.12)}`,
                fontWeight: 800,
              }}
            />
          </Stack>
        </Box>

        <Divider />

        <Box
          sx={{
            px: { xs: 2, md: 2.6 },
            py: 1.6,
            bgcolor: BRAND.bg,
            borderBottom: `1px solid ${BRAND.borderSoft}`,
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', lg: 'center' }}
            spacing={1.5}
          >
            <Box>
              <Typography
                sx={{
                  color: BRAND.dark,
                  fontSize: 13,
                  fontWeight: 800,
                  mb: 0.8,
                }}
              >
                Filtrar resultados
              </Typography>

              <ToggleButtonGroup
                value={tipoSeleccionado}
                exclusive
                onChange={handleTipoChange}
                sx={{
                  p: 0.35,
                  borderRadius: 2,
                  bgcolor: '#FFFFFF',
                  border: `1px solid ${BRAND.border}`,
                  overflowX: 'auto',
                  maxWidth: '100%',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 0,
                    mx: 0.15,
                  },
                }}
              >
                {['Gobierno', 'Gestion', 'Calidad'].map((tipo) => {
                  const config = brandConfig[tipo];

                  return (
                    <ToggleButton
                      key={tipo}
                      value={tipo}
                      sx={{
                        px: { xs: 1.4, md: 1.8 },
                        py: 0.75,
                        borderRadius: '7px !important',
                        textTransform: 'none',
                        color: BRAND.muted,
                        fontWeight: 750,
                        fontSize: 13,
                        gap: 0.7,
                        whiteSpace: 'nowrap',
                        '&.Mui-selected': {
                          bgcolor: BRAND.blue,
                          color: '#fff',
                          '&:hover': {
                            bgcolor: BRAND.blue,
                          },
                        },
                      }}
                    >
                      {React.cloneElement(config.icon, { fontSize: 'small' })}
                      {config.shortLabel}
                    </ToggleButton>
                  );
                })}

                {canShowGenerales && (
                  <ToggleButton
                    value="Generales"
                    sx={{
                      px: { xs: 1.4, md: 1.8 },
                      py: 0.75,
                      borderRadius: '7px !important',
                      textTransform: 'none',
                      color: BRAND.muted,
                      fontWeight: 750,
                      fontSize: 13,
                      gap: 0.7,
                      whiteSpace: 'nowrap',
                      '&.Mui-selected': {
                        bgcolor: BRAND.blue,
                        color: '#fff',
                        '&:hover': {
                          bgcolor: BRAND.blue,
                        },
                      },
                    }}
                  >
                    {React.cloneElement(brandConfig.Generales.icon, { fontSize: 'small' })}
                    Generales
                  </ToggleButton>
                )}
              </ToggleButtonGroup>
            </Box>

            {tipoSeleccionado !== 'Generales' && (
              <TextField
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar proceso o abreviatura"
                size="small"
                sx={{
                  width: { xs: '100%', lg: 330 },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#FFFFFF',
                    borderRadius: 2,
                    fontSize: 13,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: BRAND.softMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            px: { xs: 2, md: 2.6 },
            py: 1.6,
            bgcolor: '#FFFFFF',
            borderBottom: `1px solid ${BRAND.borderSoft}`,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(3, 1fr)',
                md: tipoSeleccionado === 'Generales'
                  ? '1.4fr 1fr 1fr'
                  : '1.4fr 1fr 1fr 1fr',
              },
              gap: 1.4,
              alignItems: 'stretch',
            }}
          >
            <Box
              sx={{
                p: 1.6,
                borderRadius: 2,
                bgcolor: '#F8FAFC',
                border: `1px solid ${BRAND.borderSoft}`,
              }}
            >
              <Typography sx={{ color: BRAND.dark, fontWeight: 800, fontSize: 14 }}>
                {selectedConfig.label}
              </Typography>

              <Typography sx={{ color: BRAND.muted, fontSize: 12.5, mt: 0.35 }}>
                Vista activa del detalle
              </Typography>
            </Box>

            {tipoSeleccionado !== 'Generales' && (
              <SummaryCard
                label="Procesos"
                value={resumenSeleccionado.procesos}
                helper="En este ámbito"
              />
            )}

            <SummaryCard
              label="Preguntas"
              value={resumenSeleccionado.preguntas}
              helper="Analizadas"
            />

            <SummaryCard label="Promedio">
              <Box sx={{ mt: 1 }}>
                <ScoreBar value={resumenSeleccionado.promedio} width="100%" />
              </Box>
            </SummaryCard>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: '#F6F8FB',
            p: { xs: 1.25, md: 1.6 },
          }}
        >
          {tipoSeleccionado === 'Generales' ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2.25,
                overflow: 'hidden',
                border: `1px solid ${BRAND.borderSoft}`,
                minHeight: 520,
              }}
            >
              <GeneralQuestionsDetail
                questions={resultadosGenerales}
                promedio={promedioGenerales}
              />
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: '350px minmax(0, 1fr)',
                  xl: '380px minmax(0, 1fr)',
                },
                gap: 1.6,
                minHeight: { xs: 'auto', md: 620 },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2.25,
                  border: `1px solid ${BRAND.borderSoft}`,
                  overflow: 'hidden',
                  bgcolor: '#F1F4F8',
                  minHeight: { xs: 'auto', md: 620 },
                }}
              >
                <Box
                  sx={{
                    px: 1.7,
                    py: 1.5,
                    bgcolor: '#FFFFFF',
                    borderBottom: `1px solid ${BRAND.borderSoft}`,
                  }}
                >
                  <Typography sx={{ color: BRAND.dark, fontWeight: 850, fontSize: 14 }}>
                    Procesos
                  </Typography>

                  <Typography sx={{ color: BRAND.muted, fontSize: 12.5, mt: 0.25 }}>
                    {procesosFiltrados.length} procesos disponibles
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 1,
                    maxHeight: { xs: 420, md: 560 },
                    overflow: 'auto',
                  }}
                >
                  {procesosFiltrados.length > 0 ? (
                    <Stack spacing={0.8}>
                      {procesosFiltrados.map((proceso) => (
                        <ProcessListItem
                          key={proceso.procesoId}
                          proceso={proceso}
                          selected={selectedProcesoId === proceso.procesoId}
                          onClick={() => setSelectedProcesoId(proceso.procesoId)}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: '#FFFFFF',
                        borderRadius: 2,
                        border: `1px solid ${BRAND.borderSoft}`,
                      }}
                    >
                      <EmptyState
                        title="Sin procesos"
                        text="No se encontraron procesos con los filtros aplicados."
                      />
                    </Paper>
                  )}
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2.25,
                  border: `1px solid ${BRAND.borderSoft}`,
                  overflow: 'hidden',
                  bgcolor: '#FFFFFF',
                  minHeight: { xs: 520, md: 620 },
                }}
              >
                <QuestionsDetail proceso={selectedProceso} />
              </Paper>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default DetallePorProceso;